import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Edit, Eye, Copy } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const TEMPLATE_LABELS = {
  interview_invite: { label: 'Interview Invitation', color: 'bg-info/20 text-info' },
  offer_letter: { label: 'Offer Letter', color: 'bg-success/20 text-success' },
  rejection: { label: 'Rejection Notice', color: 'bg-error/20 text-error' },
  screening_passed: { label: 'Screening Passed', color: 'bg-warning/20 text-warning' },
};

export function EmailTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', body: '' });
  const [previewContent, setPreviewContent] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/recruitment/email-templates');
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleSaveTemplate = async () => {
    try {
      await api.put(`/recruitment/email-templates/${editingTemplate.template_key}`, editForm);
      toast.success('Template saved');
      setEditDialog(false);
      fetchTemplates();
    } catch { toast.error('Failed to save'); }
  };

  const handlePreview = async (tmpl) => {
    try {
      const res = await api.post(`/recruitment/generate-email/${tmpl.template_key}`, {
        applicant_name: 'John Smith',
        job_title: 'Software Engineer',
        department: 'Engineering',
        interview_date: '2026-04-01',
        interview_time: '10:00 AM',
        interview_location: 'Office - Meeting Room 2',
        interviewer_name: 'HR Manager',
        start_date: '2026-05-01',
        salary: 'AED 15,000',
      });
      setPreviewContent(res.data);
      setPreviewDialog(true);
    } catch { toast.error('Failed to generate preview'); }
  };

  if (loading) return <div className="text-center py-8 text-sm text-muted-foreground">Loading templates...</div>;

  return (
    <div className="space-y-4" data-testid="email-templates-section">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><FileText size={16} /> Recruitment Email Templates</CardTitle>
            <p className="text-xs text-muted-foreground">Customize emails sent during the hiring process</p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {templates.map(tmpl => {
              const meta = TEMPLATE_LABELS[tmpl.template_key] || { label: tmpl.template_key, color: 'bg-muted text-foreground' };
              return (
                <div key={tmpl.template_key} className="border rounded-lg p-4 hover:bg-muted/50 transition" data-testid={`template-${tmpl.template_key}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{meta.label}</span>
                        <Badge className={`text-[10px] border-0 ${meta.color}`}>{tmpl.template_key}</Badge>
                        {tmpl.is_default && <Badge variant="outline" className="text-[10px]">Default</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">Subject: {tmpl.subject}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handlePreview(tmpl)} data-testid={`preview-${tmpl.template_key}`}><Eye size={12} className="mr-1" />Preview</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingTemplate(tmpl); setEditForm({ subject: tmpl.subject, body: tmpl.body }); setEditDialog(true); }} data-testid={`edit-${tmpl.template_key}`}><Edit size={12} className="mr-1" />Edit</Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg" data-testid="edit-template-dialog">
          <DialogHeader><DialogTitle>Edit Template: {TEMPLATE_LABELS[editingTemplate?.template_key]?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={editForm.subject} onChange={e => setEditForm({ ...editForm, subject: e.target.value })} data-testid="template-subject" /></div>
            <div><Label>Body</Label>
              <Textarea value={editForm.body} onChange={e => setEditForm({ ...editForm, body: e.target.value })} rows={12} className="font-mono text-xs" data-testid="template-body" />
            </div>
            <p className="text-[10px] text-muted-foreground">Variables: {'{applicant_name}'}, {'{job_title}'}, {'{company_name}'}, {'{department}'}, {'{interview_date}'}, {'{interview_time}'}, {'{interview_location}'}, {'{start_date}'}, {'{salary}'}</p>
            <Button onClick={handleSaveTemplate} className="w-full" data-testid="save-template-btn">Save Template</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-lg" data-testid="preview-template-dialog">
          <DialogHeader><DialogTitle>Email Preview</DialogTitle></DialogHeader>
          {previewContent && (
            <div className="space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg border">
                <div className="text-xs text-muted-foreground mb-1">Subject:</div>
                <div className="text-sm font-medium">{previewContent.subject}</div>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-xs text-muted-foreground mb-1">Body:</div>
                <pre className="text-sm whitespace-pre-wrap font-sans">{previewContent.body}</pre>
              </div>
              <Button variant="outline" className="w-full" onClick={() => { navigator.clipboard.writeText(`Subject: ${previewContent.subject}\n\n${previewContent.body}`); toast.success('Copied to clipboard'); }}><Copy size={14} className="mr-1" />Copy Email</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
